import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import NotificationSettings from '../components/common/NotificationSettings';
import { 
    Calendar, Clock, Users, FileText, AlertTriangle, CheckCircle, 
    ChevronRight, Settings, ClipboardList, Eye, MessageSquare, LogOut, User,
    Bell, Moon, Sun, ScanLine
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ConsultantDashboardPage() {
    const { user, authToken, logout } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [referrals, setReferrals] = useState([]);
    const [imagingFlags, setImagingFlags] = useState({});
    const [queue, setQueue] = useState([]);
    const [schedule, setSchedule] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [showNotifications, setShowNotifications] = useState(false);
    const [stats, setStats] = useState({
        pending: 0,
        accepted: 0,
        scheduled: 0,
        urgent: 0
    });

    useEffect(() => {
        fetchData();
        fetchNotifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchImagingFlags = async (refs, headers) => {
        try {
            const ids = (refs || []).map((r) => r.id);
            if (ids.length === 0) return;
            const res = await axios.post(`${API}/api/nir/imaging-flags`, { referral_ids: ids }, { headers });
            setImagingFlags(res.data?.flags || {});
        } catch (_) { /* non-fatal background enrichment */ }
    };

    const fetchData = async () => {
        try {
            const headers = { Authorization: `Bearer ${authToken}` };
            
            // Fetch referrals with mine_only=true to only show patients assigned to this consultant
            const [referralsRes, queueRes, scheduleRes] = await Promise.all([
                axios.get(`${API}/api/referrals?mine_only=true`, { headers }),
                axios.get(`${API}/api/appointment-queue`, { headers }),
                axios.get(`${API}/api/consultant/schedule`, { headers })
            ]);

            setReferrals(referralsRes.data);
            setQueue(queueRes.data);
            setSchedule(scheduleRes.data);
            fetchImagingFlags(referralsRes.data, headers);

            // Calculate stats
            const pending = referralsRes.data.filter(r => r.status === 'pending').length;
            const accepted = referralsRes.data.filter(r => r.status === 'accepted').length;
            const scheduled = referralsRes.data.filter(r => r.status === 'scheduled').length;
            const urgent = referralsRes.data.filter(r => r.urgency_level === 'urgent').length;
            
            setStats({ pending, accepted, scheduled, urgent });
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const fetchNotifications = async () => {
        try {
            const headers = { Authorization: `Bearer ${authToken}` };
            const response = await axios.get(`${API}/api/notifications`, { headers });
            setNotifications(response.data || []);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const markAllRead = async () => {
        try {
            const headers = { Authorization: `Bearer ${authToken}` };
            await axios.put(`${API}/api/notifications/mark-all-read`, {}, { headers });
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        } catch (error) {
            console.error('Error marking notifications as read:', error);
        }
    };

    const unreadCount = Array.isArray(notifications) ? notifications.filter(n => !n.is_read).length : 0;

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const handleReview = async (referralId, action, urgencyOverride = null) => {
        try {
            const headers = { Authorization: `Bearer ${authToken}` };
            await axios.put(`${API}/api/referrals/${referralId}/review`, {
                action,
                urgency_override: urgencyOverride,
                notes: ''
            }, { headers });
            
            toast.success(`Referral ${action}ed successfully`);
            fetchData();
        } catch (error) {
            toast.error('Failed to review referral');
        }
    };

    const handleAddToQueue = async (referralId) => {
        try {
            const headers = { Authorization: `Bearer ${authToken}` };
            await axios.post(`${API}/api/referrals/${referralId}/queue`, {}, { headers });
            toast.success('Added to appointment queue');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add to queue');
        }
    };

    const getUrgencyColor = (level) => {
        switch (level) {
            case 'urgent': return 'bg-red-500';
            case 'semi_urgent': return 'bg-amber-500';
            case 'moderate': return 'bg-amber-500'; // backward compatibility
            case 'semi_urgent': return 'bg-amber-500';
            default: return 'bg-emerald-500';
        }
    };

    const pendingReferrals = referrals.filter(r => r.status === 'pending');
    const acceptedReferrals = referrals.filter(r => r.status === 'accepted');

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6" data-testid="consultant-dashboard">
            <div className="max-w-7xl mx-auto">
                <header className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Consultant Dashboard</h1>
                        <p className="text-slate-600 dark:text-slate-400">Welcome back, {user?.full_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Settings */}
                        <Dialog>
                            <DialogTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    data-testid="settings-btn"
                                    title="Settings"
                                >
                                    <Settings className="w-5 h-5" />
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-lg">
                                <DialogHeader>
                                    <DialogTitle>Settings</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 mt-4">
                                    <NotificationSettings />
                                </div>
                            </DialogContent>
                        </Dialog>

                        {/* Dark Mode Toggle */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={toggleTheme}
                            data-testid="theme-toggle-btn"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </Button>

                        {/* Notifications */}
                        <div className="relative">
                            <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setShowNotifications(!showNotifications)}
                                className="relative"
                                data-testid="notifications-btn"
                            >
                                <Bell className="w-5 h-5" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                                        {unreadCount}
                                    </span>
                                )}
                            </Button>
                            
                            {showNotifications && (
                                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50">
                                    <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">Notifications</h3>
                                        <Button variant="ghost" size="sm" onClick={markAllRead}>
                                            Mark all read
                                        </Button>
                                    </div>
                                    <div className="max-h-96 overflow-y-auto">
                                        {(!Array.isArray(notifications) || notifications.length === 0) ? (
                                            <div className="p-4 text-center text-slate-500 dark:text-slate-400">
                                                No notifications
                                            </div>
                                        ) : (
                                            notifications.slice(0, 10).map((notif) => (
                                                <div 
                                                    key={notif.id}
                                                    className={`p-4 border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer ${
                                                        !notif.is_read ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                                                    }`}
                                                    onClick={() => notif.referral_id && navigate(`/referral/${notif.referral_id}`)}
                                                >
                                                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{notif.title}</p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{notif.message}</p>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <Link
                            to="/profile"
                            className="hidden sm:flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 ml-2 hover:text-teal-700 hover:bg-teal-50 dark:hover:bg-slate-800 rounded-md px-2 py-1 transition-colors"
                            data-testid="profile-link"
                            title="Profile & Security"
                        >
                            <User className="w-4 h-4" />
                            <span>{user?.full_name}</span>
                            <Badge variant="outline" className="ml-2 capitalize">
                                {user?.role?.replace('_', ' ')}
                            </Badge>
                        </Link>

                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/profile')}
                            data-testid="profile-btn"
                            className="sm:hidden text-slate-600 dark:text-slate-400"
                            title="Profile & Security"
                        >
                            <User className="w-5 h-5" />
                        </Button>
                        
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={handleLogout}
                            data-testid="logout-btn"
                            className="text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </div>
                </header>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <Card>
                        <CardContent className="p-4 flex items-center space-x-4">
                            <div className="p-3 bg-amber-100 rounded-lg">
                                <Clock className="h-6 w-6 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Pending Review</p>
                                <p className="text-2xl font-bold text-slate-900">{stats.pending}</p>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card>
                        <CardContent className="p-4 flex items-center space-x-4">
                            <div className="p-3 bg-emerald-100 rounded-lg">
                                <CheckCircle className="h-6 w-6 text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Accepted</p>
                                <p className="text-2xl font-bold text-slate-900">{stats.accepted}</p>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card>
                        <CardContent className="p-4 flex items-center space-x-4">
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <Calendar className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Scheduled</p>
                                <p className="text-2xl font-bold text-slate-900">{stats.scheduled}</p>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card>
                        <CardContent className="p-4 flex items-center space-x-4">
                            <div className="p-3 bg-red-100 rounded-lg">
                                <AlertTriangle className="h-6 w-6 text-red-600" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Urgent Cases</p>
                                <p className="text-2xl font-bold text-slate-900">{stats.urgent}</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <Tabs defaultValue="pending" className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="pending">Pending Review ({pendingReferrals.length})</TabsTrigger>
                        <TabsTrigger value="accepted">Accepted ({acceptedReferrals.length})</TabsTrigger>
                        <TabsTrigger value="queue">Appointment Queue ({queue.length})</TabsTrigger>
                        <TabsTrigger value="schedule">My Schedule</TabsTrigger>
                    </TabsList>

                    {/* Pending Referrals */}
                    <TabsContent value="pending">
                        <Card>
                            <CardHeader>
                                <CardTitle>Referrals Awaiting Review</CardTitle>
                                <CardDescription>Review and accept/reject incoming referrals</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {pendingReferrals.length === 0 ? (
                                    <div className="text-center py-8 text-slate-500">
                                        No pending referrals
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {pendingReferrals.map(referral => (
                                            <div 
                                                key={referral.id} 
                                                className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                                                data-testid={`pending-referral-${referral.id}`}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h3 className="font-semibold text-lg">
                                                            {referral.patient.first_name} {referral.patient.last_name}
                                                        </h3>
                                                        <p className="text-slate-600">
                                                            {referral.condition.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        </p>
                                                        <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                                                            <span>BMI: {referral.bmi}</span>
                                                            <span>|</span>
                                                            <span>Score: {referral.urgency_score}/100</span>
                                                            <span>|</span>
                                                            <span>From: Dr. {referral.referring_doctor_name}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Badge className={`${getUrgencyColor(referral.urgency_level)} text-white`}>
                                                            {referral.urgency_level}
                                                        </Badge>
                                                        {imagingFlags[referral.id] > 0 && (
                                                            <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200" data-testid={`imaging-flag-${referral.id}`}>
                                                                <ScanLine className="w-3 h-3 mr-1" />
                                                                Imaging on file
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="flex gap-2 mt-4">
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline"
                                                        onClick={() => navigate(`/referral/${referral.id}`)}
                                                    >
                                                        <Eye className="h-4 w-4 mr-1" />
                                                        View Details
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline"
                                                        onClick={() => window.open(`${API}/api/referrals/${referral.id}/pre-clinic-note`, '_blank')}
                                                    >
                                                        <FileText className="h-4 w-4 mr-1" />
                                                        Pre-Clinic Note
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        className="bg-emerald-600 hover:bg-emerald-700"
                                                        onClick={() => handleReview(referral.id, 'accept')}
                                                    >
                                                        <CheckCircle className="h-4 w-4 mr-1" />
                                                        Accept
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline"
                                                        onClick={() => navigate(`/referral/${referral.id}?action=request-tests`)}
                                                    >
                                                        <ClipboardList className="h-4 w-4 mr-1" />
                                                        Request Tests
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Accepted Referrals */}
                    <TabsContent value="accepted">
                        <Card>
                            <CardHeader>
                                <CardTitle>Accepted Referrals</CardTitle>
                                <CardDescription>Add to queue or override urgency level</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {acceptedReferrals.length === 0 ? (
                                    <div className="text-center py-8 text-slate-500">
                                        No accepted referrals awaiting queue
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {acceptedReferrals.map(referral => (
                                            <div 
                                                key={referral.id}
                                                className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h3 className="font-semibold">
                                                            {referral.patient.first_name} {referral.patient.last_name}
                                                        </h3>
                                                        <p className="text-sm text-slate-600">
                                                            {referral.condition.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        </p>
                                                    </div>
                                                    <Badge className={`${getUrgencyColor(referral.consultant_urgency_override || referral.urgency_level)} text-white`}>
                                                        {referral.consultant_urgency_override || referral.urgency_level}
                                                    </Badge>
                                                </div>
                                                <div className="flex gap-2 mt-4">
                                                    <Button 
                                                        size="sm"
                                                        onClick={() => handleAddToQueue(referral.id)}
                                                    >
                                                        Add to Queue
                                                    </Button>
                                                    <select 
                                                        className="px-3 py-1 text-sm border rounded-md"
                                                        onChange={(e) => handleReview(referral.id, 'accept', e.target.value)}
                                                        defaultValue=""
                                                    >
                                                        <option value="" disabled>Override Urgency</option>
                                                        <option value="urgent">Urgent (6 weeks)</option>
                                                        <option value="semi_urgent">Semi-Urgent (13 weeks)</option>
                                                        <option value="routine">Routine (26 weeks)</option>
                                                    </select>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Appointment Queue */}
                    <TabsContent value="queue">
                        <Card>
                            <CardHeader>
                                <CardTitle>Appointment Queue</CardTitle>
                                <CardDescription>Patients waiting for appointments by urgency</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {queue.length === 0 ? (
                                    <div className="text-center py-8 text-slate-500">
                                        No patients in queue
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {queue.map((item, index) => (
                                            <div 
                                                key={item.id}
                                                className="flex items-center justify-between border rounded-lg p-3"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <span className="text-lg font-bold text-slate-400">#{index + 1}</span>
                                                    <div>
                                                        <p className="font-medium">{item.patient_name}</p>
                                                        <p className="text-sm text-slate-500">
                                                            Target: {item.target_date}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <Badge className={`${getUrgencyColor(item.urgency_level)} text-white`}>
                                                        {item.urgency_level}
                                                    </Badge>
                                                    <Button size="sm" variant="outline">
                                                        Schedule
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Schedule Management */}
                    <TabsContent value="schedule">
                        <Card>
                            <CardHeader>
                                <CardTitle>Clinic Schedule</CardTitle>
                                <CardDescription>Manage your clinic availability</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {schedule && Object.keys(schedule).length > 0 ? (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-sm text-slate-500">Clinic Frequency</p>
                                                <p className="font-medium">{schedule.clinic_frequency}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-slate-500">New Patients Per Clinic</p>
                                                <p className="font-medium">{schedule.new_patients_per_clinic}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-slate-500">Clinic Days</p>
                                                <p className="font-medium">{schedule.clinic_days?.join(', ') || 'Not set'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-slate-500">Clinic Hours</p>
                                                <p className="font-medium">{schedule.clinic_start_time} - {schedule.clinic_end_time}</p>
                                            </div>
                                        </div>
                                        {schedule.blocked_dates?.length > 0 && (
                                            <div>
                                                <p className="text-sm text-slate-500 mb-2">Blocked Dates</p>
                                                <div className="flex flex-wrap gap-2">
                                                    {schedule.blocked_dates.map(date => (
                                                        <Badge key={date} variant="secondary">{date}</Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <p className="text-slate-500 mb-4">No schedule configured</p>
                                        <Button onClick={() => navigate('/settings')}>
                                            <Settings className="h-4 w-4 mr-2" />
                                            Configure Schedule
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
